/**
 * The plan's space type (garage, workshop, bathroom …), picked by icon.
 *
 *   SpaceMenu   — the header button: the current space's icon and "Space",
 *                 opening a list of the spaces.
 *   SpacePicker — the grid of icon tiles in the room dialog.
 *
 * The spaces are the estimator's own (data/presets SPACE_TYPES, with their
 * lucide icons); their ids are the staging kits' keys, so a choice here is
 * the kit the room is furnished with.
 */

import React from 'react';
import { Bath, Building2, Car, Hammer, Home, LayoutGrid, WashingMachine, Warehouse } from 'lucide-react';
import { SPACE_TYPES } from '../../../data/presets';
import { DESIGNER_STRINGS as S } from '../strings';
import { DesignerMenu, DesignerMenuItem } from './DesignerMenu';
import './designerHeader.css';

type IconComponent = typeof Car;

const ICONS: Record<string, IconComponent> = { Car, Home, Building2, WashingMachine, Hammer, Warehouse, Bath, LayoutGrid };

export const spaceLabel = (id: string): string => S.spaceTypeName[id] ?? SPACE_TYPES.find((s) => s.id === id)?.label ?? id;

export const SpaceIcon: React.FC<{ id: string; size?: number }> = ({ id, size = 14 }) => {
  const name = SPACE_TYPES.find((s) => s.id === id)?.icon ?? 'LayoutGrid';
  const Icon = ICONS[name] ?? LayoutGrid;
  return <Icon size={size} />;
};

export interface SpaceMenuProps {
  value: string;
  onChange: (id: string) => void;
}

export const SpaceMenu: React.FC<SpaceMenuProps> = ({ value, onChange }) => (
  <DesignerMenu icon={<SpaceIcon id={value} />} label={S.spaceType} title={`${S.spaceType}: ${spaceLabel(value)}`} menuLabel={S.spaceType}>
    {(close) => SPACE_TYPES.map((s) => (
      <DesignerMenuItem
        key={s.id}
        icon={<SpaceIcon id={s.id} />}
        label={spaceLabel(s.id)}
        active={s.id === value}
        onClick={() => { close(); if (s.id !== value) onChange(s.id); }}
      />
    ))}
  </DesignerMenu>
);

export interface SpacePickerProps {
  value: string;
  onChange: (id: string) => void;
  ariaLabel?: string;
}

export const SpacePicker: React.FC<SpacePickerProps> = ({ value, onChange, ariaLabel }) => (
  <div className="dh-space-grid" role="radiogroup" aria-label={ariaLabel ?? S.spaceType}>
    {SPACE_TYPES.map((s) => (
      <button
        key={s.id}
        type="button"
        role="radio"
        aria-checked={s.id === value}
        className={`dh-space-tile${s.id === value ? ' is-active' : ''}`}
        title={spaceLabel(s.id)}
        onClick={() => onChange(s.id)}
      >
        <SpaceIcon id={s.id} size={18} />
        <span>{spaceLabel(s.id)}</span>
      </button>
    ))}
  </div>
);

export default SpaceMenu;
